import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {CopyButton} from 'src/components/copy-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {GRANT_STATUS_CAPTIONS} from 'src/modules/entitlements/constants';
import {getGrantsByIds} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {getGrantResources} from 'src/openapi/admin/apis/GrantResourcesApi';
import {Grant} from 'src/openapi/admin/models/Grant';
import {GrantStatus} from 'src/openapi/admin/models/GrantStatus';
import {useAppDispatch} from 'src/store/hooks';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {
    next,
    selectDrawerDetails,
    setGrantDetails,
} from '../entitlement-drawer.slice';
import {EntitlementType as EntitlementTypes} from '../typedef';

import {ResourcesList} from './components/resources-list';
import {GrantSkeleton} from './grant.skeleton';

import {
    entitlementDetailsViewHierarchyItem,
    entitlementDetailsViewToggleLabel,
} from './entitlement-details-view.module.styl';

type Props = {
    grantId: string,
};

export const GrantView = ({grantId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const {createAbortSignal} = useAbortSignal();

    const {grantDetails} = useSelector(selectDrawerDetails);
    const [resourceListExpanded, setResourceListExpanded] = useState(true);

    useEffect(() => {
        const getGrantDetails = async () => {
            const signal = createAbortSignal();

            let loadedGrant: Grant = {
                id: '',
                name: '',
                description: '',
                status: GrantStatus.ALLOW,
                requiredApprovals: false,
            };
            const grantResponse = await apiDispatch(getGrantsByIds({requestBody: [grantId]}, {signal}));
            if (grantResponse.payload.error) {
                dispatch(setGrantDetails({error: grantResponse.payload.data}));
                return;
            } else {
                loadedGrant = grantResponse.payload.data[grantId];
            }

            let loadedResources = [];
            const grantResources = await apiDispatch(getGrantResources(
                {grantId, pageNumber: 1, pageSize: 250}, {signal},
            ));
            if (grantResources.payload.success) {
                loadedResources = grantResources.payload.data.resources.sort((a, b) => {
                    return a.name.localeCompare(b.name, undefined, {numeric: true, sensitivity: 'base'});
                });
            } else {
                dispatch(setGrantDetails({error: grantResources.payload.data}));
                return;
            }

            dispatch(setGrantDetails({data: {...loadedGrant, resources: loadedResources}}));
        };

        getGrantDetails();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleResourceList = useCallback(() => {
        setResourceListExpanded(prevState => !prevState);
    }, [setResourceListExpanded]);

    const openChildResource = useCallback((entitlementId: string) => {
        dispatch(next({entitlementType: EntitlementTypes.RESOURCE, entitlementId}));
    }, [dispatch]);

    return (
        <Skeleton shown={!grantDetails} view={<GrantSkeleton/>}>
            <ServerResponseHandler serverError={grantDetails?.error || null} smallView={true}>
                {grantDetails?.data ? (
                    <Group direction="v" alignItems="start">
                        <div style={{width: 350}}>
                            <Label>Grant Description</Label>
                            {grantDetails.data.description}
                        </div>
                        <div style={{width: 350}}>
                            <Label>Manageable Grant</Label>
                            {grantDetails.data.requiredApprovals ? 'Yes' : 'No'}
                        </div>
                        <div>
                            <Label>Status</Label>
                            {GRANT_STATUS_CAPTIONS[grantDetails.data.status]}
                        </div>
                        <div>
                            <Label>Restriction</Label>
                            {grantDetails.data.daysToDelay !== null
                                ? `DaysToDelay: ${grantDetails.data.daysToDelay}`
                                : '--'}
                        </div>
                        <div>
                            <Label>External ID</Label>
                            <Group alignItems="center">
                                <div style={{maxWidth: 300}}>
                                    <EllipsisTextHint title={grantDetails.data.id}>
                                        {grantDetails.data.id}
                                    </EllipsisTextHint>
                                </div>
                                <CopyButton copiedText={grantDetails.data.id} entityName="Grant External ID"/>
                            </Group>
                        </div>
                        {grantDetails.data.resources.length ? (
                            <div>
                                <Group
                                    className={entitlementDetailsViewHierarchyItem}
                                    onClick={toggleResourceList}
                                    alignItems="center"
                                    gap="s">
                                    <Icon type={resourceListExpanded ? Icons.ANGLE_UP : Icons.ANGLE_DOWN}/>
                                    <b>Resources</b>
                                </Group>
                                {resourceListExpanded ? (
                                    <div className={entitlementDetailsViewToggleLabel}>
                                        <ResourcesList
                                            resources={grantDetails.data.resources}
                                            onResourceClick={openChildResource}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </Group>
                ) : <div/>}
            </ServerResponseHandler>
        </Skeleton>
    );
};
